// ── Status & Enum Types ─────────────────────────────────────────────────────

export type PartnershipStatus =
  | 'of_interest'
  | 'prospecting'
  | 'outreach'
  | 'negotiating'
  | 'active'
  | 'on_hold'
  | 'paused'
  | 'declined'
  | 'ended';

export type PlatformType =
  | 'curseforge'
  | 'modrinth'
  | 'technic'
  | 'atlauncher'
  | 'ftb'
  | 'other';

export type CreatorType = 'individual' | 'team';

export type CreatorCodeStatus = 'active' | 'expired' | 'revoked';

export type IncentiveType =
  | 'free_server'
  | 'monetization_concession'
  | 'revenue_share'
  | 'marketing'
  | 'custom';

export type IncentiveStatus = 'active' | 'pending' | 'expired' | 'revoked';

export type ServerLinkStatus = 'active' | 'provisioning' | 'decommissioned';

// ── Core Entity Types ───────────────────────────────────────────────────────

export interface PartnershipCreator {
  id: string;
  name: string;
  type: CreatorType;
  contact_info: string | null;
  platform_links: string | null; // JSON string
  website: string | null;
  notes: string | null;
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
}

export interface PartnershipModpack {
  id: string;
  creator_id: string;
  name: string;
  description: string | null;
  platform_url: string | null;
  platform_slug: string | null;
  platform_type: PlatformType | null;
  logo_url: string | null;
  player_count_estimate: number | null;
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
}

export interface Partnership {
  id: string;
  creator_id: string | null;
  modpack_id: string | null;
  status: PartnershipStatus;
  assigned_to: string | null;
  priority: number;
  summary: string | null;
  deal_terms: string | null; // JSON string
  contact_date: number | null;
  response_date: number | null;
  start_date: number | null;
  end_date: number | null;
  decline_reason: string | null;
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
}

export interface PartnershipFull extends Partnership {
  modpack: PartnershipModpack | null;
  creator: PartnershipCreator;
  tags: PartnershipTag[];
  codes: PartnershipCreatorCode[];
  incentives: PartnershipIncentive[];
  servers: PartnershipServerLink[];
  notes_count: number;
  comments_count: number;
  latest_activity: PartnershipActivityLogEntry[];
}

export interface PartnershipCreatorCode {
  id: string;
  partnership_id: string;
  code: string;
  discount_percent: number;
  revenue_share_percent: number;
  status: CreatorCodeStatus;
  notes: string | null;
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
}

export interface PartnershipIncentive {
  id: string;
  partnership_id: string;
  type: IncentiveType;
  title: string;
  description: string;
  value: string | null; // JSON string
  status: IncentiveStatus;
  start_date: number | null;
  end_date: number | null;
  created_at: number;
  created_by: string;
  updated_at: number | null;
  updated_by: string | null;
}

export interface PartnershipNote {
  id: string;
  partnership_id: string;
  title: string | null;
  content: string;
  pinned: number; // 0 or 1
  author_id: string;
  created_at: number;
  updated_at: number | null;
  updated_by: string | null;
}

export interface PartnershipComment {
  id: string;
  partnership_id: string;
  content: string;
  author_id: string;
  created_at: number;
}

export interface PartnershipTag {
  id: string;
  name: string;
  color: string; // hex #RRGGBB
  description: string | null;
  created_at: number;
  created_by: string;
}

export interface PartnershipTagLink {
  partnership_id: string;
  tag_id: string;
  added_by: string;
  added_at: number;
}

export interface PartnershipServerLink {
  id: string;
  partnership_id: string;
  fqdn: string | null;
  cluster_id: string | null;
  server_name: string | null;
  status: ServerLinkStatus;
  notes: string | null;
  linked_by: string;
  linked_at: number;
  updated_at: number | null;
  updated_by: string | null;
}

export interface PartnershipCalendarLink {
  partnership_id: string;
  event_id: string;
  linked_by: string;
  linked_at: number;
}

export interface PartnershipActivityLogEntry {
  id: string;
  partnership_id: string;
  action: string;
  description: string;
  old_value: string | null;
  new_value: string | null;
  actor_id: string;
  created_at: number;
}

// ── Aggregate / Full Types ──────────────────────────────────────────────────

export interface CreatorFull extends PartnershipCreator {
  modpacks: PartnershipModpack[];
  partnerships: Partnership[];
}

export interface ModpackFull extends PartnershipModpack {
  creator: PartnershipCreator;
  partnerships: Partnership[];
}

export interface PartnershipStats {
  total: number;
  by_status: Record<string, number>;
  by_priority: Record<string, number>;
  recently_active: number;
  total_creators: number;
  total_modpacks: number;
  total_active_codes: number;
  total_active_incentives: number;
  total_server_links: number;
}

// ── Paginated Response ──────────────────────────────────────────────────────

export interface PaginatedResponse<T> {
  page: number;
  limit: number;
  total: number;
  total_pages?: number;
  results: T[];
}

// ── Request Types ───────────────────────────────────────────────────────────

export interface CreateCreatorRequest {
  name: string;
  type: CreatorType;
  contact_info?: string;
  platform_links?: string;
  website?: string;
  notes?: string;
}

export interface UpdateCreatorRequest {
  name?: string;
  type?: CreatorType;
  contact_info?: string | null;
  platform_links?: string | null;
  website?: string | null;
  notes?: string | null;
}

export interface CreateModpackRequest {
  creator_id: string;
  name: string;
  description?: string;
  platform_url?: string;
  platform_slug?: string;
  platform_type?: PlatformType;
  logo_url?: string;
  player_count_estimate?: number;
}

export interface UpdateModpackRequest {
  creator_id?: string;
  name?: string;
  description?: string | null;
  platform_url?: string | null;
  platform_slug?: string | null;
  platform_type?: PlatformType | null;
  logo_url?: string | null;
  player_count_estimate?: number | null;
}

export interface CreatePartnershipRequest {
  creator_id?: string | null;
  modpack_id?: string | null;
  status?: PartnershipStatus;
  assigned_to?: string | null;
  priority?: number;
  summary?: string;
  deal_terms?: string;
  contact_date?: number | null;
  response_date?: number | null;
  start_date?: number | null;
  end_date?: number | null;
  decline_reason?: string | null;
  tag_ids?: string[];
}

export interface UpdatePartnershipRequest {
  creator_id?: string | null;
  modpack_id?: string | null;
  status?: PartnershipStatus;
  assigned_to?: string | null;
  priority?: number;
  summary?: string | null;
  deal_terms?: string | null;
  contact_date?: number | null;
  response_date?: number | null;
  start_date?: number | null;
  end_date?: number | null;
  decline_reason?: string | null;
}

export interface CreateCreatorCodeRequest {
  code: string;
  discount_percent: number;
  revenue_share_percent: number;
  status?: CreatorCodeStatus;
  notes?: string;
}

export interface UpdateCreatorCodeRequest {
  code?: string;
  discount_percent?: number;
  revenue_share_percent?: number;
  status?: CreatorCodeStatus;
  notes?: string | null;
}

export interface CreateIncentiveRequest {
  type: IncentiveType;
  title: string;
  description: string;
  value?: string;
  status?: IncentiveStatus;
  start_date?: number | null;
  end_date?: number | null;
}

export interface UpdateIncentiveRequest {
  type?: IncentiveType;
  title?: string;
  description?: string;
  value?: string | null;
  status?: IncentiveStatus;
  start_date?: number | null;
  end_date?: number | null;
}

export interface CreateNoteRequest {
  title?: string;
  content: string;
}

export interface UpdateNoteRequest {
  title?: string | null;
  content?: string;
  pinned?: number; // 0 or 1
}

export interface CreateServerLinkRequest {
  fqdn?: string;
  cluster_id?: string;
  server_name?: string;
  status?: ServerLinkStatus;
  notes?: string;
}

export interface UpdateServerLinkRequest {
  fqdn?: string | null;
  cluster_id?: string | null;
  server_name?: string | null;
  status?: ServerLinkStatus;
  notes?: string | null;
}

export interface CreateTagRequest {
  name: string;
  color: string;
  description?: string;
}

export interface UpdateTagRequest {
  name?: string;
  color?: string;
  description?: string | null;
}

// ── Filter / Query Types ────────────────────────────────────────────────────

export interface PartnershipListParams {
  status?: PartnershipStatus;
  assigned_to?: string;
  tag_id?: string;
  priority_min?: number;
  priority_max?: number;
  modpack_id?: string;
  creator_id?: string;
  has_servers?: boolean;
  search?: string;
  sort_by?: 'created_at' | 'priority' | 'status' | 'start_date' | 'updated_at';
  sort_order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// ── Constants ───────────────────────────────────────────────────────────────

export const PARTNERSHIP_STATUSES: PartnershipStatus[] = [
  'of_interest',
  'prospecting',
  'outreach',
  'negotiating',
  'active',
  'on_hold',
  'paused',
  'declined',
  'ended',
];

export const PLATFORM_TYPES: PlatformType[] = [
  'curseforge',
  'modrinth',
  'technic',
  'atlauncher',
  'ftb',
  'other',
];

export const INCENTIVE_TYPES: IncentiveType[] = [
  'free_server',
  'monetization_concession',
  'revenue_share',
  'marketing',
  'custom',
];

export const CREATOR_CODE_STATUSES: CreatorCodeStatus[] = [
  'active',
  'expired',
  'revoked',
];

export const INCENTIVE_STATUSES: IncentiveStatus[] = [
  'active',
  'pending',
  'expired',
  'revoked',
];

export const SERVER_LINK_STATUSES: ServerLinkStatus[] = [
  'active',
  'provisioning',
  'decommissioned',
];
